import { create } from 'zustand';
import type { TableCommand } from '../editor/table';
import {
  PAGE_MARGINS,
  PAGE_SIZES,
  type PageMargins,
  type PageSize,
} from '../lib/export/printLayout';
import {
  deriveTitle,
  type DocSessionState,
  type Permission,
  type SwitchPane,
  type ThemePreference,
  type ViewMode,
} from '@markbird/shared';
import type { AuthUser } from '../lib/auth';
import { resolveLocale, setActiveLocale, type LocalePref } from '../i18n';

/** Live-sync connection status shown to the user. */

/** A persisted setting that must be one of `allowed`, else `fallback`. */
function storedChoice<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback;
  const value = localStorage.getItem(key);
  return allowed.includes(value as T) ? (value as T) : fallback;
}

export type SyncStatus = 'off' | 'connecting' | 'synced' | 'offline' | 'error';

/** The panels vault mode adds beside the editor. */
export type VaultPanel = 'tags' | 'links' | 'graph' | 'journal';

/** Library sort order (pinned always sorts first, regardless). */
export type SortBy = 'updated' | 'created' | 'title' | 'manual';
const SORT_OPTIONS: readonly SortBy[] = ['updated', 'created', 'title', 'manual'];

/**
 * Global UI + active-document state. The Dexie library (`lib/library.ts`) is
 * the source of truth for the persisted document set; this store holds the
 * in-memory active document plus transient UI state, and the doc controller
 * (`hooks/useDocController.ts`) syncs the two via debounced autosave.
 */
export interface ActiveDoc {
  id: string;
  title: string;
  content: string;
}

const EMPTY_DOC: ActiveDoc = { id: '', title: '', content: '' };

/** Sidebar width bounds, shared by the store's clamp and the drag handle. */
export const SIDEBAR_MIN = 200;
export const SIDEBAR_MAX = 520;
export const SIDEBAR_DEFAULT = 260;

/**
 * A pending in-app prompt/confirm. `key` identifies the request so the dialog
 * can reseed its field exactly once; `resolve` settles the promise handed back
 * by `askPrompt` / `askConfirm` in `lib/dialogs.ts`.
 */
export interface PromptRequest {
  key: symbol;
  title: string;
  initial?: string;
  confirmLabel?: string;
  resolve: (value: string | null) => void;
}

export interface ConfirmRequest {
  key: symbol;
  title: string;
  body?: string;
  confirmLabel?: string;
  danger?: boolean;
  /** Notice rather than a question: one button, nothing to decline. */
  acknowledge?: boolean;
  resolve: (ok: boolean) => void;
}

interface StoreState {
  activeDoc: ActiveDoc;
  /** True once the user manually renames; suppresses title auto-derivation. */
  titleManuallySet: boolean;
  /** Session state to restore into the editor after a doc loads. */
  pendingSession: DocSessionState | null;
  viewMode: ViewMode;
  switchPane: SwitchPane;
  theme: ThemePreference;
  sidebarOpen: boolean;
  vimMode: boolean;
  paletteOpen: boolean;

  /** Editor settings (persisted locally). */
  editorFontSize: number;
  lineNumbers: boolean;
  settingsOpen: boolean;
  setEditorFontSize: (px: number) => void;
  setLineNumbers: (on: boolean) => void;
  toggleLineNumbers: () => void;
  setSettingsOpen: (open: boolean) => void;

  /** Undo-delete toast (soft-deleted doc awaiting restore). */
  undoDelete: { docId: string; title: string } | null;
  setUndoDelete: (v: { docId: string; title: string } | null) => void;

  /** Accounts (phase B). `user` null = local-only. */
  user: AuthUser | null;
  authReady: boolean;
  authOpen: boolean;
  setUser: (u: AuthUser | null) => void;
  setAuthReady: (v: boolean) => void;
  setAuthOpen: (v: boolean) => void;

  /** Opens the Markdown import file dialog (registered by EditorPage). */

  /** Opens the editor's find & replace panel (registered by EditorPage). */
  openSearch: (() => void) | null;
  setOpenSearch: (fn: (() => void) | null) => void;
  /** Runs a table command in the editor; false when the cursor is not in a table. */
  runTable: ((command: TableCommand) => boolean) | null;
  setRunTable: (fn: ((command: TableCommand) => boolean) | null) => void;

  /** Outline (table-of-contents) panel visibility. */
  outlineOpen: boolean;
  toggleOutline: () => void;
  setOutlineOpen: (open: boolean) => void;

  /**
   * Vault mode: the knowledge-base surface (tag pane, link hygiene, graph).
   *
   * Off by default, because a focused editor and a personal wiki want opposite
   * things from the same screen. Wiki *semantics* — wikilinks, embeds, tags —
   * are always on, since a document must render the same either way; this flag
   * only governs the panels built on top of them.
   */
  vaultMode: boolean;
  toggleVaultMode: () => void;

  /** Which vault panel is showing, or null when the panel is closed. */
  vaultPanel: VaultPanel | null;
  setVaultPanel: (panel: VaultPanel | null) => void;

  /** Scroll editor + preview to a source line (registered by EditorPage). */
  goToLine: ((line: number) => void) | null;
  setGoToLine: (fn: ((line: number) => void) | null) => void;

  /** Share flow state (surfaced by a single ShareDialog). */
  shareUrl: string | null;
  sharePermission: Permission;
  /** When the link stops working; anonymous links always have an expiry. */
  shareExpiresAt: number | null;
  shareBusy: boolean;
  shareError: string | null;
  setShareBusy: (busy: boolean) => void;
  setShareResult: (
    url: string | null,
    error: string | null,
    permission?: Permission,
    expiresAt?: number | null,
  ) => void;
  clearShare: () => void;

  /** Manage-shares dialog (list / copy / revoke). */
  sharesOpen: boolean;
  setSharesOpen: (open: boolean) => void;
  /** Trash browser dialog visibility. */
  trashOpen: boolean;
  setTrashOpen: (open: boolean) => void;
  /** Template picker dialog visibility. */
  templatesOpen: boolean;
  setTemplatesOpen: (open: boolean) => void;

  /** Share-with-options dialog (permission + expiry). */
  shareOptionsOpen: boolean;
  setShareOptionsOpen: (open: boolean) => void;

  /** Live sync status shown in the account area. */
  syncStatus: SyncStatus;
  setSyncStatus: (s: SyncStatus) => void;

  /**
   * Reload a document into the open editor if it's the active one (registered
   * by the doc controller). Called by the sync engine when a remote change for
   * the currently-open doc lands, so edits from another device appear live.
   */
  reloadOpenDoc: ((id: string) => void) | null;
  setReloadOpenDoc: (fn: ((id: string) => void) | null) => void;

  /** Distraction-free writing: hides sidebar/toolbar/status chrome. */
  focusMode: boolean;
  toggleFocusMode: () => void;
  setFocusMode: (on: boolean) => void;

  /** Typewriter scrolling: keeps the caret line vertically centered. */
  typewriter: boolean;
  toggleTypewriter: () => void;

  /** Keyboard-shortcuts cheat-sheet overlay. */
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;

  /** Transient sync notice (e.g. the open doc changed on another device). */
  syncNotice: string | null;
  setSyncNotice: (msg: string | null) => void;

  /**
   * A sync conflict: a remote (last-write-wins) update overwrote a locally
   * diverged document. The previous local version was snapshotted, so the user
   * can review/restore it. `null` = no pending conflict.
   */
  /** The account's synced notes are encrypted and this device has no key yet. */
  vaultLocked: boolean;
  setVaultLocked: (locked: boolean) => void;
  syncConflict: { docId: string } | null;
  setSyncConflict: (c: { docId: string } | null) => void;

  /** Account settings dialog (export all, delete account). */
  accountDialogOpen: boolean;
  setAccountDialogOpen: (open: boolean) => void;

  /** Version-history dialog for the active document. */
  historyOpen: boolean;
  setHistoryOpen: (open: boolean) => void;

  /** Import-from-URL dialog. */
  importOpen: boolean;
  setImportOpen: (open: boolean) => void;
  importUrlOpen: boolean;
  setImportUrlOpen: (open: boolean) => void;

  /** Colour theme used for HTML/PDF exports (default light). */
  exportTheme: 'light' | 'dark';
  setExportTheme: (t: 'light' | 'dark') => void;
  /** PDF page setup, persisted like the export theme. */
  exportPageSize: PageSize;
  setExportPageSize: (size: PageSize) => void;
  exportMargins: PageMargins;
  setExportMargins: (margins: PageMargins) => void;
  exportToc: boolean;
  setExportToc: (on: boolean) => void;

  /** Active tag filter in the library sidebar (null = all documents). */
  tagFilter: string | null;
  setTagFilter: (tag: string | null) => void;

  /** Library sort order (pinned documents always sort first). */
  sortBy: SortBy;
  setSortBy: (s: SortBy) => void;

  /** Collapsed folder paths in the library tree. */
  collapsedFolders: string[];
  toggleFolder: (path: string) => void;

  /** Document id whose "move to folder" dialog is open (null = closed). */
  moveToFolderFor: string | null;
  setMoveToFolderFor: (id: string | null) => void;

  /** Whether the "publish a folder as a site" dialog is open. */
  publishSiteOpen: boolean;
  setPublishSiteOpen: (open: boolean) => void;

  /** Library sidebar width in px (drag-resizable, persisted). */
  sidebarWidth: number;
  setSidebarWidth: (px: number) => void;

  /** Pending in-app `prompt()` / `confirm()` (null = none open). */
  prompt: PromptRequest | null;
  setPrompt: (r: PromptRequest | null) => void;
  confirm: ConfirmRequest | null;
  setConfirm: (r: ConfirmRequest | null) => void;

  /** Open document tabs (ids), in order. The active tab is `activeDoc.id`. */
  openTabs: string[];
  addTab: (id: string) => void;
  /** Close a tab; returns the neighbouring id to activate, or null if none. */
  closeTab: (id: string) => string | null;

  /** Document id whose tag editor is open (null = closed). */
  tagsEditorFor: string | null;
  setTagsEditorFor: (id: string | null) => void;

  /** UI language preference (auto follows the browser). */
  locale: LocalePref;
  setLocale: (pref: LocalePref) => void;

  /** Autosave state shown beside the title. */
  saveState: 'saved' | 'saving' | 'offline';
  setSaveState: (s: 'saved' | 'saving' | 'offline') => void;

  /** Viewport is narrow (phone/tablet): force single-pane, collapse sidebar. */
  isNarrow: boolean;
  setNarrow: (v: boolean) => void;

  setContent: (content: string) => void;
  setTitle: (title: string) => void;
  loadDoc: (doc: ActiveDoc, session: DocSessionState | null) => void;
  consumePendingSession: () => DocSessionState | null;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  setSwitchPane: (pane: SwitchPane) => void;
  toggleSwitchPane: () => void;
  setTheme: (theme: ThemePreference) => void;
  toggleSidebar: () => void;
  setSidebar: (open: boolean) => void;
  toggleVim: () => void;
  /** `documents` is the ⌘O quick switcher: the palette with only documents in it. */
  paletteMode: 'all' | 'documents';
  setPaletteOpen: (open: boolean, mode?: 'all' | 'documents') => void;
}

const INITIAL_LOCALE: LocalePref =
  (typeof localStorage !== 'undefined'
    ? (localStorage.getItem('markbird.locale') as LocalePref | null)
    : null) ?? 'auto';
// Set the active translation locale before any component renders.
setActiveLocale(resolveLocale(INITIAL_LOCALE));
if (typeof document !== 'undefined') document.documentElement.lang = resolveLocale(INITIAL_LOCALE);

export const useStore = create<StoreState>((set, get) => ({
  activeDoc: EMPTY_DOC,
  titleManuallySet: false,
  pendingSession: null,
  viewMode: 'split',
  switchPane: 'source',
  theme:
    (typeof localStorage !== 'undefined'
      ? (localStorage.getItem('markbird.theme') as ThemePreference | null)
      : null) ?? 'system',
  sidebarOpen: true,
  // Persisted like the other editor settings: someone who uses Vim keys
  // should not have to turn them back on after every reload.
  vimMode: typeof localStorage !== 'undefined' && localStorage.getItem('markbird.vimMode') === '1',
  paletteOpen: false,
  paletteMode: 'all',

  editorFontSize:
    (typeof localStorage !== 'undefined' ? Number(localStorage.getItem('markbird.fontSize')) : 0) ||
    15,
  lineNumbers:
    typeof localStorage !== 'undefined' && localStorage.getItem('markbird.lineNumbers') === '1',
  settingsOpen: false,
  setEditorFontSize: (px) => {
    const clamped = Math.min(24, Math.max(11, Math.round(px)));
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('markbird.fontSize', String(clamped));
    set({ editorFontSize: clamped });
  },
  setLineNumbers: (on) => {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('markbird.lineNumbers', on ? '1' : '0');
    set({ lineNumbers: on });
  },
  toggleLineNumbers: () => {
    const on = !get().lineNumbers;
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('markbird.lineNumbers', on ? '1' : '0');
    set({ lineNumbers: on });
  },
  setSettingsOpen: (open) => set({ settingsOpen: open }),

  undoDelete: null,
  setUndoDelete: (v) => set({ undoDelete: v }),

  user: null,
  authReady: false,
  authOpen: false,
  setUser: (u) => set({ user: u }),
  setAuthReady: (v) => set({ authReady: v }),
  setAuthOpen: (v) => set({ authOpen: v }),

  shareUrl: null,
  sharePermission: 'read',
  shareExpiresAt: null,
  shareBusy: false,
  shareError: null,
  setShareBusy: (busy) => set({ shareBusy: busy }),
  setShareResult: (url, error, permission = 'read', expiresAt = null) =>
    set({
      shareUrl: url,
      shareError: error,
      sharePermission: permission,
      shareExpiresAt: expiresAt,
      shareBusy: false,
    }),
  clearShare: () => set({ shareUrl: null, shareError: null }),

  sharesOpen: false,
  setSharesOpen: (open) => set({ sharesOpen: open }),

  trashOpen: false,
  setTrashOpen: (open) => set({ trashOpen: open }),

  templatesOpen: false,
  setTemplatesOpen: (open) => set({ templatesOpen: open }),

  shareOptionsOpen: false,
  setShareOptionsOpen: (open) => set({ shareOptionsOpen: open }),

  syncStatus: 'off',
  setSyncStatus: (s) => set({ syncStatus: s }),

  reloadOpenDoc: null,
  setReloadOpenDoc: (fn) => set({ reloadOpenDoc: fn }),

  focusMode: false,
  toggleFocusMode: () => set((s) => ({ focusMode: !s.focusMode })),
  setFocusMode: (on) => set({ focusMode: on }),

  typewriter:
    typeof localStorage !== 'undefined' && localStorage.getItem('markbird.typewriter') === '1',
  toggleTypewriter: () => {
    const on = !get().typewriter;
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('markbird.typewriter', on ? '1' : '0');
    set({ typewriter: on });
  },

  shortcutsOpen: false,
  setShortcutsOpen: (open) => set({ shortcutsOpen: open }),

  syncNotice: null,
  setSyncNotice: (msg) => set({ syncNotice: msg }),

  vaultLocked: false,
  setVaultLocked: (locked) => set({ vaultLocked: locked }),
  syncConflict: null,
  setSyncConflict: (c) => set({ syncConflict: c }),

  accountDialogOpen: false,
  setAccountDialogOpen: (open) => set({ accountDialogOpen: open }),

  historyOpen: false,
  setHistoryOpen: (open) => set({ historyOpen: open }),

  importOpen: false,
  setImportOpen: (open) => set({ importOpen: open }),
  importUrlOpen: false,
  setImportUrlOpen: (open) => set({ importUrlOpen: open }),

  exportTheme:
    (typeof localStorage !== 'undefined'
      ? (localStorage.getItem('markbird.exportTheme') as 'light' | 'dark' | null)
      : null) ?? 'light',
  setExportTheme: (theme) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('markbird.exportTheme', theme);
    set({ exportTheme: theme });
  },

  // Stored values are checked against the allowed lists: storage outlives
  // versions, and a stale value must not become an invalid @page rule.
  exportPageSize: storedChoice('markbird.exportPageSize', PAGE_SIZES, 'auto'),
  setExportPageSize: (size) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('markbird.exportPageSize', size);
    set({ exportPageSize: size });
  },
  exportMargins: storedChoice('markbird.exportMargins', PAGE_MARGINS, 'normal'),
  setExportMargins: (margins) => {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('markbird.exportMargins', margins);
    set({ exportMargins: margins });
  },
  exportToc:
    typeof localStorage !== 'undefined' && localStorage.getItem('markbird.exportToc') === '1',
  setExportToc: (on) => {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('markbird.exportToc', on ? '1' : '0');
    set({ exportToc: on });
  },

  tagFilter: null,
  setTagFilter: (tag) => set({ tagFilter: tag }),

  // Persisted: a manual order that is gone after a reload would be pointless.
  sortBy: storedChoice('markbird.sortBy', SORT_OPTIONS, 'updated'),
  setSortBy: (s) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('markbird.sortBy', s);
    set({ sortBy: s });
  },

  collapsedFolders: [],
  toggleFolder: (path) =>
    set((st) => ({
      collapsedFolders: st.collapsedFolders.includes(path)
        ? st.collapsedFolders.filter((p) => p !== path)
        : [...st.collapsedFolders, path],
    })),

  moveToFolderFor: null,
  setMoveToFolderFor: (id) => set({ moveToFolderFor: id }),

  publishSiteOpen: false,
  setPublishSiteOpen: (open) => set({ publishSiteOpen: open }),

  sidebarWidth:
    (typeof localStorage !== 'undefined'
      ? Number(localStorage.getItem('markbird.sidebarWidth'))
      : 0) || SIDEBAR_DEFAULT,
  setSidebarWidth: (px) => {
    const clamped = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, Math.round(px)));
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('markbird.sidebarWidth', String(clamped));
    set({ sidebarWidth: clamped });
  },

  prompt: null,
  setPrompt: (r) => set({ prompt: r }),
  confirm: null,
  setConfirm: (r) => set({ confirm: r }),

  openTabs: [],
  addTab: (id) => set((st) => (st.openTabs.includes(id) ? st : { openTabs: [...st.openTabs, id] })),
  closeTab: (id) => {
    const tabs = get().openTabs;
    const idx = tabs.indexOf(id);
    const next = tabs.filter((t) => t !== id);
    set({ openTabs: next });
    if (get().activeDoc.id !== id) return null;
    return next[idx] ?? next[idx - 1] ?? next[next.length - 1] ?? null;
  },

  tagsEditorFor: null,
  setTagsEditorFor: (id) => set({ tagsEditorFor: id }),

  locale: INITIAL_LOCALE,
  setLocale: (pref) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('markbird.locale', pref);
    const resolved = resolveLocale(pref);
    setActiveLocale(resolved);
    if (typeof document !== 'undefined') document.documentElement.lang = resolved;
    set({ locale: pref });
  },

  saveState: 'saved',
  setSaveState: (s) => set({ saveState: s }),

  isNarrow: false,
  setNarrow: (v) => set({ isNarrow: v }),

  setContent: (content) =>
    set((s) => {
      const title = s.titleManuallySet ? s.activeDoc.title : deriveTitle(content, 'Untitled');
      return { activeDoc: { ...s.activeDoc, content, title } };
    }),

  setTitle: (title) =>
    set((s) => ({ activeDoc: { ...s.activeDoc, title }, titleManuallySet: true })),

  loadDoc: (doc, session) =>
    set({
      activeDoc: doc,
      // If the stored title differs from what we'd derive, treat it as manual.
      titleManuallySet: doc.title !== deriveTitle(doc.content, 'Untitled'),
      pendingSession: session,
      viewMode: session?.viewMode ?? get().viewMode,
      switchPane: session?.switchPane ?? get().switchPane,
    }),

  consumePendingSession: () => {
    const s = get().pendingSession;
    if (s) set({ pendingSession: null });
    return s;
  },

  setViewMode: (mode) => set({ viewMode: mode }),
  toggleViewMode: () => set((s) => ({ viewMode: s.viewMode === 'split' ? 'switch' : 'split' })),
  setSwitchPane: (pane) => set({ switchPane: pane }),
  toggleSwitchPane: () =>
    set((s) => ({ switchPane: s.switchPane === 'source' ? 'preview' : 'source' })),
  setTheme: (theme) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('markbird.theme', theme);
    set({ theme });
  },
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebar: (open) => set({ sidebarOpen: open }),
  toggleVim: () => {
    const on = !get().vimMode;
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('markbird.vimMode', on ? '1' : '0');
    set({ vimMode: on });
  },
  setPaletteOpen: (open, mode = 'all') =>
    set(open ? { paletteOpen: true, paletteMode: mode } : { paletteOpen: false }),

  openSearch: null,
  setOpenSearch: (fn) => set({ openSearch: fn }),
  runTable: null,
  setRunTable: (fn) => set({ runTable: fn }),

  outlineOpen: false,
  toggleOutline: () => set((s) => ({ outlineOpen: !s.outlineOpen })),
  setOutlineOpen: (open) => set({ outlineOpen: open }),

  vaultMode: typeof localStorage !== 'undefined' && localStorage.getItem('markbird.vault') === '1',
  toggleVaultMode: () =>
    set((s) => {
      const on = !s.vaultMode;
      if (typeof localStorage !== 'undefined')
        localStorage.setItem('markbird.vault', on ? '1' : '0');
      // Leaving vault mode takes its panel with it, so the flag and the layout
      // can never disagree.
      return { vaultMode: on, vaultPanel: on ? s.vaultPanel : null };
    }),

  vaultPanel: null,
  setVaultPanel: (panel) => set({ vaultPanel: panel }),

  goToLine: null,
  setGoToLine: (fn) => set({ goToLine: fn }),
}));
